import {
	Component,
	computed,
	inject,
	Injectable,
	OnInit,
	signal
} from "@angular/core";

import {
	FormsModule
} from "@angular/forms";

import {
	MAT_DIALOG_DATA,
	MatDialog,
	MatDialogRef
} from "@angular/material/dialog";

import {
	CommonModule
} from "@angular/common";

import {
	TranslateModule,
	TranslateService
} from "@ngx-translate/core";

import {
	MatCheckboxModule
} from "@angular/material/checkbox";

import {
	MatButtonModule
} from "@angular/material/button";

import {
	MatIconModule
} from "@angular/material/icon";

import {
	MatTooltipModule
} from "@angular/material/tooltip";

import {
	BaseDialogService,
	NonNullableConfigData,
	TilbyDialogContentComponent,
	TilbyDialogToolbarComponent
} from "@tilby/tilby-ui-lib/components/tilby-dialog";

import {
	lastValueFrom
} from "rxjs";

import {
	restManager
} from "app/ajs-upgraded-providers";

import {
	groupBy,
	keyBy,
} from "src/app/shared/utils";

import {
	downloadFile
} from "src/app/shared/data-conversion-utils";

import {
	EnvironmentInfoService
} from "src/app/core";

import {
	ItemDataOperationsService
} from "src/app/features/items";

import {
	MatFormField,
	MatOption,
	MatSelect,
} from "@angular/material/select";

import {
	AlertDialogService
} from "../alert-dialog";

import {
	MatSnackBar
} from "@angular/material/snack-bar";

export interface Scale {
	name: string;
	type: string;
	ip_address: string;
	isChecked?: boolean;
	status?: 'idle' | 'loading' | 'success' | 'error';
	exitCode?: number;
	errorMessage?: string;
}

export namespace ScaleImportDialog {
	export type InputData = {
		scales: Scale[];
	};
}

const supportedScales = new Set([
	'helmac',
	'dibal'
]);

const helmacErrorMessages: { [key: number]: string } = {
	0: 'ITEMS.SCALE_IMPORT.STATUS.OK',
	1: 'ITEMS.SCALE_IMPORT.STATUS.ERR_RUNTIME',
	2: 'ITEMS.SCALE_IMPORT.STATUS.ERR_COMMAND_LINE',
	3: 'ITEMS.SCALE_IMPORT.STATUS.ERR_SERVER_NOT_FOUND',
	4: 'ITEMS.SCALE_IMPORT.STATUS.ERR_SERVER_CONNECT',
	5: 'ITEMS.SCALE_IMPORT.STATUS.ERR_MODEM_CONNECT',
	6: 'ITEMS.SCALE_IMPORT.STATUS.ERR_SCALE_CONNECT',
	7: 'ITEMS.SCALE_IMPORT.STATUS.ERR_SCALE_ON_NETWORK',
	8: 'ITEMS.SCALE_IMPORT.STATUS.ERR_SCALE_SEND',
	9: 'ITEMS.SCALE_IMPORT.STATUS.ERR_SCALE_RECEIVE',
	10: 'ITEMS.SCALE_IMPORT.STATUS.ERR_TICKET_OPENED',
	11: 'ITEMS.SCALE_IMPORT.STATUS.ERR_NO_TICKET_HISTORY',
	12: 'ITEMS.SCALE_IMPORT.STATUS.ERR_UNKNOWN_PROCEDURE',
	13: 'ITEMS.SCALE_IMPORT.STATUS.ERR_FILE_IMPORT',
	14: 'ITEMS.SCALE_IMPORT.STATUS.ERR_FILE_CSV_SETTINGS',
	15: 'ITEMS.SCALE_IMPORT.STATUS.ERR_NOTHING_TO_DO',
	16: 'ITEMS.SCALE_IMPORT.STATUS.ERR_FILE_SCRIPT_SETTINGS',
	17: 'ITEMS.SCALE_IMPORT.STATUS.ERR_PROTOCOL_TOTAL_PLU',
	18: 'ITEMS.SCALE_IMPORT.STATUS.ERR_PROTOCOL_TOTAL_GRUPPI_PLU',
	19: 'ITEMS.SCALE_IMPORT.STATUS.ERR_PROTOCOL_TOTAL_OPERATORE',
	20: 'ITEMS.SCALE_IMPORT.STATUS.ERR_PROTOCOL_TOTAL_ALIQUOTA_IVA',
	21: 'ITEMS.SCALE_IMPORT.STATUS.ERR_PROTOCOL_TOTAL_PAGAMENTI',
	22: 'ITEMS.SCALE_IMPORT.STATUS.ERR_PROTOCOL_TOTAL_LOTTI_TRAC',
	23: 'ITEMS.SCALE_IMPORT.STATUS.ERR_PROTOCOL_TOTAL_ORARIO',
	24: 'ITEMS.SCALE_IMPORT.STATUS.ERR_PROTOCOL_TICKET_HISTORY',
	25: 'ITEMS.SCALE_IMPORT.STATUS.ERR_WRITE_FILE_EXPORT',
	26: 'ITEMS.SCALE_IMPORT.STATUS.ERR_SCALE_STATUS',
};

export namespace HelmacImport {
	export interface ScaleResult {
		ip: string;
		exitCode: number;
		success: boolean;
	}

	export interface ProcessPluSuccess {
		success: true;
		partial: false;
		results: ScaleResult[];
		error?: never;
	}

	export interface ProcessPluPartial {
		success: false;
		partial: true;
		results: ScaleResult[];
		error?: never;
	}

	export interface ProcessPluError {
		success: false;
		error: string;
		partial?: never;
		results?: never;
	}

	export type ProcessPluResponse = ProcessPluSuccess | ProcessPluPartial | ProcessPluError;
}

@Component({
	standalone: true,
	selector: 'app-scale-import-dialog',
	templateUrl: './scale-import-dialog.component.html',
	styleUrls: ['./scale-import-dialog.component.scss'],
	imports: [
		CommonModule,
		FormsModule,
		MatButtonModule,
		MatCheckboxModule,
		MatFormField,
		MatIconModule,
		MatOption,
		MatSelect,
		MatTooltipModule,
		TilbyDialogContentComponent,
		TilbyDialogToolbarComponent,
		TranslateModule,
	]
})
export class ScaleImportDialogComponent implements OnInit {
	private readonly alertDialogService = inject(AlertDialogService);
	private readonly itemDataOperationsService = inject(ItemDataOperationsService);
	private readonly environmentInfoService = inject(EnvironmentInfoService);
	private readonly restManager = inject(restManager);
	private readonly snackBar = inject(MatSnackBar);
	private readonly translateService = inject(TranslateService);

	protected readonly dialogRef = inject(MatDialogRef<ScaleImportDialogComponent>);
	protected readonly data: ScaleImportDialog.InputData = inject(MAT_DIALOG_DATA);
	protected readonly priceLists = this.itemDataOperationsService.getPriceLists();
	protected scales = structuredClone(this.data.scales);
	protected selectedPriceList = this.priceLists[0].index;

	protected readonly downloadInProgress = signal(false);
	protected readonly inProgress = computed(() => this.downloadInProgress() || this.processInProgress());
	protected readonly processInProgress = signal(false);

	protected readonly globalError = signal<string | null>(null);

	ngOnInit() {
		for (const scale of this.scales) {
			scale.isChecked = false;
		}
	}

	private async fetchScalesPLUs(scaleTypes: string[]) {
		const plusByType = new Map<string, string>();

		for (const scaleType of scaleTypes) {
			const PLUs = await this.fetchPLUsForScale(scaleType);

			if (PLUs) {
				plusByType.set(scaleType, PLUs);
			}
		}

		return plusByType;
	}

	private async fetchPLUsForScale(scaleType: string) {
		if (!supportedScales.has(scaleType)) {
			return null;
		}

		const PLUs = await this.restManager.getOne('items/scales_export', scaleType, { pricelist: this.selectedPriceList })
			.then((res: any) => res?.message);

		if (PLUs) {
			return PLUs;
		}

		return null;
	}

	protected async downloadPLUs() {
		this.downloadInProgress.set(true);

		try {
			const scaleTypes = Object.keys(this.getScalesByType());
	
			for (const scaleType of scaleTypes) {
				const scalePLUs = await this.fetchPLUsForScale(scaleType);
	
				if (!scalePLUs) {
					this.alertDialogService.openDialog({
						data: {
							messageLabel: 'ITEMS.SCALE_IMPORT.ERROR.NO_PLU_DATA',
						}
					});
				} else {
					if (scaleType === 'helmac' && this.environmentInfoService.isElectronApp() && this.environmentInfoService.isWindows()) {
						await window.require('electron').ipcRenderer.invoke('download-plu-helmac', { csvContent: scalePLUs }).then((res: any) => {
							const message = res?.success 
								? this.translateService.instant('ITEMS.SCALE_IMPORT.DOWNLOAD_PLU_SUCCESS', { downloadPath: res.downloadPath })
								: this.translateService.instant('ITEMS.SCALE_IMPORT.DOWNLOAD_PLU_ERROR');

							this.snackBar.open(message, undefined, {
								duration: 3000
							});
						});
					} else {
						this.downloadCsv(scalePLUs, `tilby_plu_${scaleType}.csv`);
					}
				}
			}
		} finally {
			this.downloadInProgress.set(false);
		}
	}

	private getScalesByType() {
		return groupBy(this.scales.filter(scale => scale.isChecked), scale => scale.type);
	}

	private downloadCsv(csvContent: string, filename: string) {
		return downloadFile(csvContent, filename, 'text/csv');
	}

	protected async import() {
		const scalesByType = this.getScalesByType();
		const scaleTypes = Object.keys(scalesByType);

		// Stop early if no scales are selected for import
		if (scaleTypes.length === 0) {
			return;
		}

		this.processInProgress.set(true);
		this.globalError.set(null);

		// Reset execution status details for all selected scales prior to starting
		this.scales.forEach(scale => {
			scale.exitCode = undefined;
			scale.errorMessage = undefined;
			scale.status = scale.isChecked ? 'loading' : undefined;
		});

		try {
			// Fetch fresh PLU data from backend API for all requested scale types
			const plusByType = await this.fetchScalesPLUs(scaleTypes);

			for (const [scaleType, scales] of Object.entries(scalesByType)) {
				const scalePLUs = plusByType.get(scaleType);

				if (!scalePLUs) {
					// Fallback: fail integration for scales of this type if PLU payload is missing
					for(const scale of scales) {
						scale.status = 'error';
						scale.errorMessage = 'NO_PLU_DATA';
					}

					continue;
				}

				switch (scaleType) {
					case 'helmac':
						// Helmac utility interactions require local environment context (Electron)
						if (!this.environmentInfoService.isElectronApp()) {
							throw new Error('UNSUPPORTED_PLATFORM');
						}

						// Fetch mapping CSV file template used for Helmac execution formatting
						const resp = await fetch('http://mediastore3.scloby.com.s3.amazonaws.com/common-assets/helmac/xxxImport_Plus.csv');

						if (!resp.ok) {
							for(const scale of scales) {
								scale.status = 'error';
								scale.errorMessage = 'SCHEMA_FETCH_FAILED';
							}
							return;
						}

						const importSchemaText = await resp.text();

						// Invoke the main Electron process channel to trigger the native Helmac executable integration
						const result: HelmacImport.ProcessPluResponse = await window.require('electron').ipcRenderer.invoke('process-plu-helmac', {
							csvContent: scalePLUs,
							ipAddresses: scales.map(s => s.ip_address),
							importSchema: importSchemaText
						});

						// Distribute results matching each hardware device back to its UI state wrapper
						if (result.success || result.partial) {
							const scalesByIP = keyBy(scales, (scale) => scale.ip_address);

							for(const res of result.results) {
								const scale = scalesByIP[res.ip];
	
								if (scale) {
									scale.status = res.success ? 'success' : 'error';
									scale.exitCode = res.exitCode;
								}
							}
						} else {
							// Handle process execution failure at scale-manager service level
							if (result.error) {
								this.globalError.set(result.error);
							}

							for(const scale of scales) {
								scale.status = 'error';
								scale.errorMessage = result.error || 'UNKNOWN_ERROR';
							}
						}
						break;
					case 'dibal':
						// Temporary placeholder: Dibal scale support is configured but pending execution code path
						for(const scale of scales) {
							scale.status = 'error';
							scale.errorMessage = 'UNSUPPORTED_SCALE_TYPE';
						}
						break;
					default:
						break;
				}
			}
		} catch (err: any) {
			// Catch unexpected logic or request failures during execution cycle
			this.globalError.set(err?.message || 'UNKNOWN_ERROR');

			for(const scale of this.scales) {
				if (scale.isChecked && scale.status === 'loading') {
					scale.status = 'error';
					scale.errorMessage = err?.message || 'UNKNOWN_ERROR';
				}
			}
		} finally {
			this.processInProgress.set(false);
		}
	}

	protected getTranslationKeyForExitCode(exitCode?: number, errorMessage?: string): string {
		if (exitCode !== undefined && helmacErrorMessages[exitCode]) {
			return helmacErrorMessages[exitCode];
		}
		if (errorMessage) {
			return `ITEMS.SCALE_IMPORT.ERROR.${errorMessage}`;
		}
		return 'ITEMS.SCALE_IMPORT.STATUS.ERR_UNKNOWN';
	}

	protected getIconForScale(scale: Scale): string {
		if (scale.status === 'loading') {
			return 'sync';
		}

		if (scale.status === 'success') {
			return 'check_circle';
		}

		// Maps scale error categories to precise visual feedback icons
		if (scale.status === 'error') {
			const code = scale.exitCode;
			const msg = scale.errorMessage;

			if (code) {
				switch (code) {
					// Network/connection failures (e.g. Server not found, scale connection drops)
					case 3: case 4: case 5: case 6: case 7:
						return 'wifi_off';
					// Data transmission errors or protocol validation failures
					case 8: case 9: case 17: case 18: case 19: case 20: case 21: case 22: case 23: case 24:
						return 'sync_problem';
					// File parsing, script, or write configuration failures
					case 13: case 14: case 16: case 25:
						return 'settings_applications';
					// Scale database lock or transactional conflict issues (e.g., ticket remains open)
					case 10:
						return 'lock';
				}
			}

			if (msg) {
				switch (msg) {
					case 'UNSUPPORTED_PLATFORM':
						return 'desktop_access_disabled';
					case 'NO_PLU_DATA':
						return 'search_off';
					case 'SCHEMA_FETCH_FAILED':
						return 'cloud_off';
					case 'UNSUPPORTED_SCALE_TYPE':
						return 'device_unknown';
				}
			}

			return 'error_outline';
		}
		return 'help_outline';
	}

	areAllScalesChecked() {
		return this.scales.every(scale => scale.isChecked);
	}

	isSomeScalesChecked() {
		const checkedCount = this.scales.filter(scale => scale.isChecked).length;
		return checkedCount > 0 && checkedCount < this.scales.length;
	}

	changeAllScalesSelection(checked: boolean) {
		this.scales.forEach(scale => scale.isChecked = checked);
	}

	isNoScaleSelected() {
		return !this.scales || !this.scales.some(scale => scale.isChecked);
	}
}

@Injectable({
	providedIn: "root",
})
export class ScaleImportDialogService extends BaseDialogService {
	private readonly dialogRef = inject(MatDialog);

	public async openDialog(config: NonNullableConfigData<ScaleImportDialog.InputData>) {
		const configEdited: NonNullableConfigData<ScaleImportDialog.InputData> = {
			...config,
			...this.switchMobileDesktopDimensions({ width: '600px' }),
			disableClose: true,
		};

		return await lastValueFrom(
			this.dialogRef.open(ScaleImportDialogComponent, configEdited).afterClosed()
		);
	}
}